<script lang="ts" setup>
import {ref, onMounted, onUnmounted, nextTick, computed, watch} from "vue";
import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import image1 from '../assets/icons/image1.png';
import image1Inactive from '../assets/icons/image1-inactive.png';
import image2 from '../assets/icons/image2.png';
import image2Inactive from '../assets/icons/image2-inactive.png';
import image3 from '../assets/icons/image3.png';
import image3Inactive from '../assets/icons/image3-inactive.png';
import {useRouter} from "vue-router";

gsap.registerPlugin(ScrollTrigger);

const router = useRouter()
const showCircle = ref(false);
const circleRefs = ref<Array<HTMLElement | null>>([]);
const arrowRefs = ref<Array<HTMLElement | null>>([]);
const isDesktop = ref(window.innerWidth >= 768);

const ctaButton = ref<HTMLElement | null>(null);

const isCircleGlowing = ref(false);

const goToContactUs = () => {
  window.open("https://app.usemotion.com/meet/toby-green-bbrz/catalyst?d=30", "_blank");
};

watch(
    () => circleRefs.value[0], // watch when this becomes available or changes
    (el) => {
      if (el) {
        isCircleGlowing.value = el.classList.contains('circle-glow');
      }
    },
    {immediate: true} // so it runs on first render too
);

// const isCircleGlowing = computed(() => circleRefs.value[0]?.classList.contains('circle-glow'));
const isTriangleGlowing = computed(() => circleRefs.value[1]?.classList.contains('triangle-glow'));
const isDiamondGlowing = computed(() => circleRefs.value[2]?.classList.contains('diamond-glow'));

// Update isDesktop on resize
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 768;
};

const handleScroll = () => {
  if (window.scrollY > 50 && !showCircle.value) {
    showCircle.value = true;
    nextTick(animateCircles);
  }
};

const animateCircles = () => {
  if (circleRefs.value.length < 3) return;

  // Create a fake scroll container
  const fakeScroll = document.createElement('div');
  fakeScroll.style.height = '1500px';
  fakeScroll.style.position = 'absolute';
  fakeScroll.style.top = '0';
  fakeScroll.style.left = '0';
  fakeScroll.style.width = '1px';
  fakeScroll.style.opacity = '0';
  document.body.appendChild(fakeScroll);

  // Pin the entire section
  ScrollTrigger.create({
    trigger: ".flywheel-section",
    start: "top top",
    end: '+=1500',
    pin: true,
    pinSpacing: true,
    anticipatePin: 1,
    // markers: true,
    toggleActions: 'play reverse play reverse',
  });

  if (isDesktop.value) {
    gsap.set(circleRefs.value[0], {x: '0', y: '0'});
    gsap.set(circleRefs.value[1], {x: '0', y: '0'});
    gsap.set(circleRefs.value[2], {x: '0', y: '0'});
  } else {
    // Optional: mobile starting positions
    gsap.set(circleRefs.value[0], {y: '0'});
    gsap.set(circleRefs.value[1], {x: '0', y: '0'});
    gsap.set(circleRefs.value[2], {x: '0', y: '0'});
  }

  // Initial fade in with no position change
  gsap.fromTo(
      circleRefs.value,
      {
        scale: 0,
        opacity: 0,
      },
      {
        scale: 1,
        opacity: 1,
        duration: 1,
        ease: "power2.out",
        stagger: 0.2,
        onComplete: startMainAnimation
      }
  );
};

const updateArrowPaths = () => {
  if (!circleRefs.value[0] || !circleRefs.value[1] || !circleRefs.value[2]) return;

  const container = document.querySelector('.circles-container');
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const circle = circleRefs.value[0].getBoundingClientRect();
  const triangle = circleRefs.value[1].getBoundingClientRect();
  const diamond = circleRefs.value[2].getBoundingClientRect();

  // Calculate center points
  const circleCenter = {
    x: (circle.left + circle.width / 2) - containerRect.left,
    y: (circle.top + circle.height / 2) - containerRect.top
  };
  const triangleCenter = {
    x: (triangle.left + triangle.width / 2) - containerRect.left,
    y: (triangle.top + triangle.height / 2) - containerRect.top
  };
  const diamondCenter = {
    x: (diamond.left + diamond.width / 2) - containerRect.left,
    y: (diamond.top + diamond.height / 2) - containerRect.top
  };

  const arrows = document.querySelectorAll('.arrow-container');
  const arrowWidth = 24;
  const arrowHeight = 28;

  // Circle to Triangle arrow
  if (arrows[0]) {
    const midPoint = {
      x: (circleCenter.x + triangleCenter.x) / 2 - arrowWidth / 2,
      y: (circleCenter.y + triangleCenter.y) / 2 - arrowHeight / 2 - 20
    };
    (arrows[0] as HTMLElement).style.transform = `translate(${midPoint.x - 1}px, ${midPoint.y + 20}px) rotate(0deg)`;
  }

  // Circle to Diamond arrow
  if (arrows[1]) {
    const midPoint = {
      x: (circleCenter.x + diamondCenter.x) / 2 - arrowWidth / 2 + 20,
      y: (circleCenter.y + diamondCenter.y) / 2 - arrowHeight / 2
    };
    (arrows[1] as HTMLElement).style.transform = `translate(${midPoint.x}px, ${midPoint.y}px) rotate(110deg)`;
  }

  // Diamond to Triangle arrow
  if (arrows[2]) {
    const midPoint = {
      x: (diamondCenter.x + triangleCenter.x) / 2 - arrowWidth / 2,
      y: (diamondCenter.y + triangleCenter.y) / 2 - arrowHeight / 2 + 20
    };
    (arrows[2] as HTMLElement).style.transform = `translate(${midPoint.x}px, ${midPoint.y}px) rotate(240deg)`;
  }
};

const startMainAnimation = () => {
  // Show intro text initially for both desktop and mobile
  document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.add('active'));

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: ".flywheel-section",
      start: "center center",
      end: "+=1500px",
      scrub: 0.5,
      onUpdate: updateArrowPaths,
      onLeaveBack: () => {
        // Remove all glows when scrolling back up
        circleRefs.value[0]?.classList.remove('circle-glow');
        circleRefs.value[2]?.classList.remove('diamond-glow');
        circleRefs.value[1]?.classList.remove('triangle-glow');
        // Hide all descriptions
        document.querySelectorAll('.description-text, .second-description-text, .third-description-text, .mobile-text')
            .forEach(el => el.classList.remove('active'));
        // Show intro text when no shapes are glowing
        document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.add('active'));
      }
    }
  });

  // Show circle content after shape animation
  gsap.to(".circle-content", {
    opacity: 1,
    duration: 0.5,
    delay: 1.5,
    ease: "power2.out"
  });

  // First spread the shapes
  if (isDesktop.value) {
    timeline
      .to(circleRefs.value[0], {
        y: -140,
        duration: 1.5,
        ease: "power1.inOut"
      })
      .to(circleRefs.value[1], {
        x: -140,
        y: 80,
        duration: 1.5,
        ease: "power1.inOut"
      }, "-=1.5")
      .to(circleRefs.value[2], {
        x: 140,
        y: 80,
        duration: 1.5,
        ease: "power1.inOut"
      }, "-=1.5");
  } else {
    // Mobile layout animation
    timeline
      .to(circleRefs.value[0], {
        y: -90,
        duration: 1.5,
        ease: "power1.inOut"
      })
      .to(circleRefs.value[1], {
        x: -100,
        y: 60,
        duration: 1.5,
        ease: "power1.inOut"
      }, "-=1.5")
      .to(circleRefs.value[2], {
        x: 100,
        y: 60,
        duration: 1.5,
        ease: "power1.inOut"
      }, "-=1.5");
  }

  // Show and animate arrows after shapes are spread
  timeline.to(".arrow-container", {
    opacity: 1,
    duration: 0.5,
    stagger: 0.1,
    ease: "power1.inOut",
    onStart: updateArrowPaths
  });

  // Add left movement for desktop only
  if (isDesktop.value) {
    timeline.to([circleRefs.value, ".arrow-container"], {
      duration: 1.5,
      ease: "power2.inOut"
    });
  }

  // Add glow and text animations for both desktop and mobile
  timeline
      // First phase - circle glow
      .to([], {
        duration: 1.5,
        ease: "power2.inOut",
        onStart: () => {
          circleRefs.value[0]?.classList.add('circle-glow');
          // Hide intro text when circle starts glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.remove('active'));
          if (isDesktop.value) {
            document.querySelectorAll('.second-description-text, .third-description-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.description-text')?.classList.add('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.mobile-text:nth-child(2)')?.classList.add('active');
          }
        },
        onReverseComplete: () => {
          circleRefs.value[0]?.classList.remove('circle-glow');
          // Show intro text when no shapes are glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.add('active'));
          if (isDesktop.value) {
            document.querySelector('.description-text')?.classList.remove('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
          }
        }
      })
      // Second phase - diamond glow
      .to([], {
        duration: 1.5,
        ease: "power2.inOut",
        onStart: () => {
          circleRefs.value[0]?.classList.remove('circle-glow');
          circleRefs.value[2]?.classList.add('diamond-glow');
          // Hide intro text when diamond starts glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.remove('active'));
          if (isDesktop.value) {
            document.querySelectorAll('.description-text, .third-description-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.second-description-text')?.classList.add('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.mobile-text:nth-child(3)')?.classList.add('active');
          }
        },
        onReverseComplete: () => {
          circleRefs.value[2]?.classList.remove('diamond-glow');
          circleRefs.value[0]?.classList.add('circle-glow');
          // Hide intro text since circle is glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.remove('active'));
          if (isDesktop.value) {
            document.querySelector('.second-description-text')?.classList.remove('active');
            document.querySelector('.description-text')?.classList.add('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.mobile-text:nth-child(2)')?.classList.add('active');
          }
        }
      })
      // Third phase - triangle glow
      .to([], {
        duration: 1.5,
        ease: "power2.inOut",
        onStart: () => {
          circleRefs.value[2]?.classList.remove('diamond-glow');
          circleRefs.value[1]?.classList.add('triangle-glow');
          // Hide intro text when triangle starts glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.remove('active'));
          if (isDesktop.value) {
            document.querySelectorAll('.description-text, .second-description-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.third-description-text')?.classList.add('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.mobile-text:nth-child(4)')?.classList.add('active');
          }
        },
        onReverseComplete: () => {
          circleRefs.value[1]?.classList.remove('triangle-glow');
          circleRefs.value[2]?.classList.add('diamond-glow');
          // Hide intro text since diamond is glowing
          document.querySelectorAll('.intro-text, .mobile-text:nth-child(1)').forEach(el => el.classList.remove('active'));
          if (isDesktop.value) {
            document.querySelector('.third-description-text')?.classList.remove('active');
            document.querySelector('.second-description-text')?.classList.add('active');
          } else {
            document.querySelectorAll('.mobile-text')
                .forEach(el => el.classList.remove('active'));
            document.querySelector('.mobile-text:nth-child(3)')?.classList.add('active');
          }
        }
      });
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("resize", handleResize);
  if (ctaButton.value) {
    const arrowLine = ctaButton.value.querySelector('.arrow-line');
    const arrowHead = ctaButton.value.querySelector('.arrow-head');

    ctaButton.value.addEventListener('mouseenter', () => {
      gsap.to(arrowLine, {
        strokeDashoffset: 0,
        duration: 0.4,
        ease: "power2.out"
      });
      gsap.to(arrowHead, {
        opacity: 1,
        duration: 0.3,
        delay: 0.25,
        ease: "power2.out"
      });
    });

    ctaButton.value.addEventListener('mouseleave', () => {
      gsap.to(arrowLine, {
        strokeDashoffset: 28,
        duration: 0.3,
        ease: "power2.in"
      });
      gsap.to(arrowHead, {
        opacity: 0,
        duration: 0.2,
        ease: "power2.in"
      });
    });
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
});
</script>

<template>
  <section class="flywheel-section">
    <div class="content-wrapper">
      <!-- Hide title on mobile -->
      <div class="title-container mx-5 lg:mx-32 xl:mx-32 pt-20 hidden md:block">
        <h1 class="text-[40px] lg:text-[5vw] xl:text-[80px] font-[500] text-white">Our flywheel.</h1>
      </div>

      <!-- Mobile text container -->
      <div class="mobile-text-container md:hidden">
        <div class="mobile-text">
          We've built a system for ever-improving creative winners. We call it the Flywheel—a performance engine that gets smarter with every iteration, allowing brands to scale Meta & TikTok month-on-month.
        </div>
        <div class="mobile-text">
          We move at speed to get creative out.

          We've built a strong and diverse
          catalogue of creators, who create
          thumb stopping content. This, coupled
          with in-house design and motion
          capabilities, makes Catalyst the go-to
          creative agency for DTC brands.
        </div>
        <div class="mobile-text">
          High quality, tried and tested formulas that will drive results: we create thousands of ads daily, enabling us to transform learnings into ads that convert for our clients.
        </div>
        <div class="mobile-text">
          We build supermetric dashboards to pull data from our creative testing, which leads us to what works and what doesn’t. This allows us to produce ever-improving creative.
        </div>
      </div>

      <!-- Desktop text containers - hidden on mobile -->
       <div class="intro-description-container hidden md:block">
        <p class="intro-text opacity-1">
          We've built a system for ever-improving creative winners. We call it the Flywheel—a performance engine that gets smarter with every iteration, allowing brands to scale Meta & TikTok month-on-month.
        </p>
       </div>
      <div class="description-container hidden md:block">
        <p class="description-text opacity-0">We move at speed to get creative out.

          We've built a strong and diverse
          catalogue of creators, who create
          thumb stopping content. This, coupled
          with in-house design and motion
          capabilities, makes Catalyst the go-to
          creative agency for DTC brands.</p>
      </div>

      <div class="second-description-container hidden md:block">
        <p class="second-description-text opacity-0">High quality, tried and tested formulas that will drive results: we create thousands of ads daily, enabling us to transform learnings into ads that convert for our clients.</p>
      </div>

      <div class="third-description-container hidden md:block">
        <p class="third-description-text opacity-0">We build supermetric dashboards to pull data from our creative testing, which leads us to what works and what doesn’t. This allows us to produce ever-improving creative.</p>
      </div>

      <!-- Shapes -->
      <div v-if="showCircle" class="circles-container relative w-full lg:w-1/2 lg:pl-[10%] xl:pl-[0] flex justify-center items-start md:h-full md:items-center lg:mx-10 xl:mx-0">
        <!-- Arrows -->
        <div v-for="i in 3" :key="`arrow-${i}`" class="arrow-container opacity-0">
          <svg fill="none" height="28" viewBox="0 0 31 37" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.16663 31.9095L23.2007 4.13769" stroke="#727272" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
            <path d="M12.4467 6.21362L21.1791 3.87376C22.6026 3.49233 24.0688 4.33884 24.4503 5.76236L26.7901 14.4948" stroke="#727272" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
          </svg>
        </div>

        <div
            v-for="(circle, index) in 3"
            :key="index"
            ref="circleRefs"
            :class="[
          'absolute w-24 h-24 bg-[#161816] opacity-0',
          index === 1 ? 'triangle-wrapper triangle-shape-glow' :
          index === 2 ? 'diamond-shape-glow' : 'rounded-full circle-shape-glow'
        ]"
        >
          <!-- Add SVG and text to first circle -->
          <div v-if="index === 0" class="circle-content opacity-0 flex flex-col items-center justify-center">
            <img :src="isCircleGlowing ? image1Inactive : image1"
                 alt="Creative at speed icon"
                 class="svg-icon w-8 h-8"/>
            <span class="circle-text">Creative <br> at speed</span>
          </div>
          <!-- Add SVG triangle for the second shape -->
          <div v-if="index === 1" class="circle-content opacity-0 flex flex-col items-center justify-center">
            <img :src="isTriangleGlowing ? image3Inactive : image3"
                 alt="Data-led action icon"
                 class="svg-icon mt-4 w-8 h-8"/>


            <span class="shape-text">Data-led <br> action</span>
          </div>
          <!-- Add SVG and text for diamond shape -->
          <div v-if="index === 2" class="circle-content opacity-0 flex flex-col items-center justify-center">
            <div class="diamond-content">
              <img :src="isDiamondGlowing ? image2Inactive : image2"
                   alt="Performance excellence icon"
                   class="svg-icon w-8 h-8"/>


              <span class="shape-text">Performance <br> excellence</span>
            </div>
          </div>
          <!-- Add SVG triangle -->
          <svg v-if="index === 1" class="triangle-svg" fill="none" height="141" viewBox="0 0 162 141" width="162" xmlns="http://www.w3.org/2000/svg">
            <path d="M76.3157 2.75609L1.56536 132.131C-0.506185 135.713 2.08324 140.196 6.22633 140.196H155.727C159.87 140.196 162.46 135.713 160.388 132.131L85.6377 2.75609C83.5661 -0.826086 78.3873 -0.826086 76.3157 2.75609Z" fill="#161816"/>
          </svg>

          <!-- <svg v-if="index === 1" class="triangle-svg" viewBox="0 0 162 141" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path class="triangle-path" d="M76.3157 2.75609L1.56536 132.131C-0.506185 135.713 2.08324 140.196 6.22633 140.196H155.727C159.87 140.196 162.46 135.713 160.388 132.131L85.6377 2.75609C83.5661 -0.826086 78.3873 -0.826086 76.3157 2.75609Z" fill="#727272"/>
            <path class="triangle-inner" d="M76.3157 2.75609L1.56536 132.131C-0.506185 135.713 2.08324 140.196 6.22633 140.196H155.727C159.87 140.196 162.46 135.713 160.388 132.131L85.6377 2.75609C83.5661 -0.826086 78.3873 -0.826086 76.3157 2.75609Z" fill="#1a1a1a" transform="scale(0.95) translate(4 4)"/>
          </svg> -->
        </div>
      </div>
    </div>
    <div class="absolute bottom-6 left-1 lg:left-0 2xl:left-0 w-full mx-4 lg:mx-32 -mt-8">
      <button ref="ctaButton" class="cta-button -mt-5" @click="goToContactUs">
        <span class="text text-[12px] lg:text-[24px]">Book a call</span>
        <svg class="arrow" fill="none" height="19" viewBox="0 0 35 19" width="35" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 9.27539H33.0682" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          <path d="M25.8932 1L32.2859 7.39262C33.328 8.4347 33.328 10.1277 32.2859 11.1698L25.8932 17.5624" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
        </svg>

      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.flywheel-section {
  margin-top: 5rem;
  position: relative;
  height: 100vh;
  background-color: #161816;
  overflow: hidden;
}

.content-wrapper {
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
}

.title-container {
  position: relative;
  z-index: 10;
}

.shape-glow {
  filter: drop-shadow(0 0 15px rgba(114, 114, 114, 0.8));
  border: none !important;
  background-color: #161816;
}

.triangle {
  display: none;
}

.triangle-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  border: none !important;
  filter: drop-shadow(0 0 15px rgba(114, 114, 114, 0.8));
}

.triangle-svg {
  width: 120px !important;
  height: 105px !important;
}

.arrow-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 28px;
  width: 24px;
  transform-origin: center;
  pointer-events: none;
  filter: drop-shadow(0 0 4px rgba(114, 114, 114, 0.4));
  z-index: 2;
}

.arrow-container svg {
  overflow: visible;
  width: 100%;
  height: 100%;
  transform-origin: center;
}

@keyframes arrowGlow {
  0%, 100% {
    filter: drop-shadow(0 0 4px rgba(114, 114, 114, 0.2));
  }
  50% {
    filter: drop-shadow(0 0 6px rgba(114, 114, 114, 0.4));
  }
}

.circle-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  z-index: 5;
}

.circle-text {
  color: #727272;
  font-size: 12px;
  text-align: center;
  font-weight: 500;
  /* white-space: nowrap; */
}

.shape-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  z-index: 5;
  pointer-events: none;
}

.shape-text {
  color: #727272;
  font-size: 10px;
  text-align: center;
  font-weight: 500;
}

.svg-icon {
  margin-bottom: 4px;
}

/* Desktop sizes */
@media (min-width: 768px) {
  .triangle-wrapper {
    width: 160px !important;
    height: 140px !important;
  }
  .triangle-svg {
    width: 160px !important;
    height: 140px !important;
  }
  .shape-glow:last-child {
    width: 120px !important;
    height: 120px !important;
    transform: rotate(45deg);
    border-radius: 4px;
  }
  .shape-glow {
    width: 140px !important;
    height: 140px !important;
  }
  .circle-text {
    color: #727272;
    font-size: 14px;
  }
  .shape-text {
    font-size: 14px;
  }
  .svg-icon {
    width: 35px;
    height: 35px;
    margin-bottom: 6px;
  }

  .svg-icon:last-child {
    width: 29px;
    height: 34px;
  }
  .arrow-container {
    height: 32px;
    width: 28px;
  }
  .circle-content {
    gap: 0.5rem;
  }
}

.triangle-path {
  fill: #727272;
  stroke: none;
}

.triangle-inner {
  fill: #161816;
  stroke: none;
  transform: scale(0.98) translate(2 2);
}

.diamond-content {
  transform: rotate(-45deg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 100%;
  height: 100%;
}

.shape-glow:last-child {
  width: 90px !important;
  height: 90px !important;
  transform: rotate(45deg);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

@media (min-width: 768px) {
  .shape-glow:last-child {
    width: 120px !important;
    height: 120px !important;
    transform: rotate(45deg);
    border-radius: 8px;
  }

  .diamond-content {
    gap: 12px;
  }
}

.circle-glow {
  filter: drop-shadow(0 0 15px rgb(82, 225, 117));
  border: none !important;
}

.circle-glow .svg-icon {
  opacity: 1;
}

.svg-icon {
  opacity: 0.4;
  transition: opacity 0.3s ease;
}

.circle-glow .circle-text {
  color: white;
}

.diamond-glow {
  filter: drop-shadow(0 0 15px rgb(60, 222, 247));
  border: none !important;
}

.diamond-glow .svg-icon {
  opacity: 1;
}

.diamond-glow .shape-text {
  color: white;
}

.triangle-glow {
  filter: drop-shadow(0 0 15px rgb(170, 77, 238));
}

.triangle-glow .svg-icon {
  opacity: 1;
}

.triangle-glow .shape-text {
  color: white;
}

.description-container {
  position: absolute;
  right: 7rem;
  top: 60%;
  transform: translateY(-60%);
  width: 35%;
  z-index: 10;
}
.intro-description-container {
  position: absolute;
  right: 7rem;
  top: 60%;
  transform: translateY(-60%);
  width: 35%;
  z-index: 10;
}

.description-text, .second-description-text, .third-description-text, .mobile-text {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.description-text.active, .second-description-text.active, .third-description-text.active, .mobile-text.active {
  opacity: 1;
  transform: scale(1) translateY(0);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.description-text, .second-description-text, .third-description-text, .intro-text {
  color: #ffffff;
  font-size: clamp(12px, 2vw, 24px);
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.mobile-text {
  position: absolute;
  text-align: left;
  left: 0;
  width: 100%;
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
  opacity: 0;
  transform: scale(0.95) translateY(10px);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.mobile-text.active {
  opacity: 1;
  transform: scale(1) translateY(0);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@media (max-width: 768px) {
  .flywheel-section {
    margin-top: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .circles-container {
    height: 100vh !important;
    min-height: 300px;
    padding-top: 0 !important;
    margin-bottom: 0;
    align-items: center !important;
    justify-content: center !important;
    transform: translateY(-8vh);
  }

  .arrow-container {
    opacity: 0.8 !important;
  }

  .arrow-container svg path {
    stroke: #727272 !important;
    stroke-width: 2px !important;
  }

  .mobile-text-container {
    position: absolute;
    top: 68vh;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    text-align: center;
    z-index: 10;
  }

  .shape-glow {
    width: 110px !important;
    height: 110px !important;
    position: absolute;
  }

  .shape-glow:last-child {
    width: 80px !important;
    height: 80px !important;
  }

  .triangle-wrapper {
    width: 120px !important;
    height: 105px !important;
    z-index: 1;
  }

  .triangle-svg {
    width: 120px !important;
    height: 105px !important;
    z-index: 1;
  }

  .triangle-shape-glow {
    width: 120px !important;
    height: 105px !important;
    z-index: 1;
  }

  .circle-text, .shape-text {
    font-size: 10px !important;
    color: #727272;
    opacity: 1;
    margin-top: 0.25rem;
    line-height: 1.2;
  }

  .circle-glow .circle-text,
  .diamond-glow .shape-text,
  .triangle-glow .shape-text {
    color: white;
  }

  .svg-icon {
    width: 24px !important;
    height: 24px !important;
    margin-bottom: 4px;
    opacity: 1;
  }

  .mobile-text {
    position: absolute;
    text-align: left;
    left: 0;
    width: 100%;
    color: #ffffff;
    font-size: 14px;
    line-height: 1.4;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
}

.mobile-svg-icon {
  /* width: 34px;
  height: 33px; */
  margin-bottom: 4px;
}

.second-description-container {
  position: absolute;
  right: 7rem;
  top: 60%;
  transform: translateY(-60%);
  width: 35%;
  z-index: 10;
}

.third-description-container {
  position: absolute;
  right: 7rem;
  top: 60%;
  transform: translateY(-60%);
  width: 35%;
  z-index: 10;
}

.cta-button {
  background: transparent;
  color: white;
  border: 1px solid #ffffff;
  padding: 8px 16px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.arrow {
  height: 16px;
  width: 30px;
}

.arrow-line {
  stroke-dasharray: 28;
  stroke-dashoffset: 28;
  transition: stroke-dashoffset 0.3s ease;
}

.arrow-head {
  opacity: 0;
}

@media only screen and (min-width: 1024px) and (max-width: 1280px) {
  .description-container {
    right: 5rem;
  }
  .second-description-container {
    right: 5rem;
  }
  .third-description-container {
    right: 5rem;
  }
}

.circle-shape-glow {
  filter: drop-shadow(0 0 15px rgba(114, 114, 114, 0.8));
  border: none !important;
  background-color: #161816;
  width: 104px !important;
  height: 104px !important;
  position: absolute;
}

.triangle-shape-glow {
  filter: drop-shadow(0 0 15px rgba(114, 114, 114, 0.8));
  border: none !important;
  background-color: transparent !important;
  width: 140px !important;
  height: 120px !important;
  position: absolute;
}

.diamond-shape-glow {
  filter: drop-shadow(0 0 15px rgba(114, 114, 114, 0.8));
  border: none !important;
  background-color: #161816;
  width: 91px !important;
  height: 91px !important;
  position: absolute;
  transform: rotate(45deg);
  border-radius: 8px;
}

/* Desktop sizes */
@media (min-width: 768px) {
  .circle-shape-glow {
    width: 140px !important;
    height: 140px !important;
  }

  .triangle-shape-glow {
    width: 160px !important;
    height: 140px !important;
  }

  .diamond-shape-glow {
    width: 120px !important;
    height: 120px !important;
  }
}

.circle-glow {
  filter: drop-shadow(0 0 15px rgb(82, 225, 117));
  border: none !important;
}

.diamond-glow {
  filter: drop-shadow(0 0 15px rgb(60, 222, 247));
  border: none !important;
}

.triangle-glow {
  filter: drop-shadow(0 0 15px rgb(170, 77, 238));
}

.intro-text {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.intro-text.active {
  opacity: 1;
  transform: scale(1) translateY(0);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>
